import React, { useRef, useState } from 'react';
import axios from 'axios';
import Button from './Button';
import { useAppSelector } from '../../hooks/redux';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

export interface UploadedFile {
  id?: string;
  type: 'image' | 'video' | 'document';
  filename: string;
  originalFilename: string;
  url: string;
  thumbnailUrl?: string;
  mimeType: string;
  size: number;
  uploadedBy?: string;
  uploadedAt?: string;
  description?: string;
  synopsis?: string;
  currentVersion?: number;
  updatedAt?: string;
  versions?: Array<{
    version: number;
    url: string;
    uploadedAt: string;
    uploadedBy: string;
    description?: string;
    synopsis?: string;
    changeNotes?: string;
  }>;
}

interface FileUploadProps {
  onFileSelect: (files: UploadedFile[]) => void;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in bytes
  maxFiles?: number;
  label?: string;
  error?: string;
  disabled?: boolean;
  autoUpload?: boolean; // If true, uploads files immediately
  projectId?: string; // Project ID for organizing files in uploads/{userId}/{projectId}/
  allowDocumentMetadata?: boolean; // If true, shows description and synopsis fields for documents
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  accept,
  multiple = false,
  maxSize = 10485760, // 10MB default
  maxFiles = 1,
  label,
  error,
  disabled = false,
  autoUpload = true,
  projectId,
  allowDocumentMetadata = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [fileMetadata, setFileMetadata] = useState<Record<string, { description: string; synopsis: string }>>({});
  const { accessToken } = useAppSelector((state) => state.auth);

  const uploadFile = async (file: File): Promise<UploadedFile> => {
    const formData = new FormData();
    formData.append('file', file);
    if (projectId) {
      formData.append('projectId', projectId);
    }

    // Pass projectId as query parameter for better reliability with multer
    const url = projectId 
      ? `${API_BASE_URL}/files/upload?projectId=${encodeURIComponent(projectId)}`
      : `${API_BASE_URL}/files/upload`;

    const response = await axios.post(
      url,
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress((prev) => ({ ...prev, [file.name]: percentCompleted }));
          }
        },
      }
    );

    return response.data.data.file;
  };

  const uploadFiles = async (files: File[]): Promise<UploadedFile[]> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    if (projectId) {
      formData.append('projectId', projectId);
    }

    // Pass projectId as query parameter for better reliability with multer
    const url = projectId 
      ? `${API_BASE_URL}/files/upload/multiple?projectId=${encodeURIComponent(projectId)}`
      : `${API_BASE_URL}/files/upload/multiple`;

    const response = await axios.post(
      url,
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            files.forEach((file) => {
              setUploadProgress((prev) => ({ ...prev, [file.name]: percentCompleted }));
            });
          }
        },
      }
    );

    return response.data.data.files;
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    const errors: string[] = [];

    // Check file count
    if (!multiple && fileArray.length > 1) {
      errors.push('Only one file is allowed');
    } else if (multiple && fileArray.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} files allowed`);
    }

    // Check file size and type
    fileArray.forEach((file) => {
      if (file.size > maxSize) {
        errors.push(`${file.name} exceeds maximum size of ${(maxSize / 1048576).toFixed(1)}MB`);
      } else if (accept && !accept.split(',').some((type) => {
        const pattern = type.trim().replace('*', '.*');
        return new RegExp(pattern).test(file.type);
      })) {
        errors.push(`${file.name} is not an accepted file type`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      console.error('File upload errors:', errors);
      // You might want to dispatch a toast notification here
      return;
    }

    if (validFiles.length > 0) {
      if (autoUpload) {
        setUploading(true);
        try {
          let uploadedFiles: UploadedFile[];
          if (validFiles.length === 1) {
            uploadedFiles = [await uploadFile(validFiles[0])];
          } else {
            uploadedFiles = await uploadFiles(validFiles);
          }
          const newSelectedFiles = [...selectedFiles, ...uploadedFiles];
          setSelectedFiles(newSelectedFiles);
          onFileSelect(newSelectedFiles);
          // Reset file input to allow selecting the same file again
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } catch (error: any) {
          console.error('Error uploading files:', error);
          // You might want to dispatch a toast notification here
        } finally {
          setUploading(false);
          setUploadProgress({});
        }
      } else {
        // If autoUpload is false, just pass File objects (for backward compatibility)
        // But this won't work with the current implementation
        onFileSelect(validFiles as any);
      }
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!disabled) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    onFileSelect(newFiles);
    // TODO: Optionally delete file from server
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
          {label}
        </label>
      )}
      
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-colors
          ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
          ${error ? 'border-red-500' : ''}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          disabled={disabled}
          className="hidden"
          aria-label={label || 'File upload'}
        />
        
        <div className="space-y-2">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={handleClick}
              disabled={disabled || uploading}
              isLoading={uploading}
            >
              {uploading ? 'Uploading...' : 'Select Files'}
            </Button>
            <p className="mt-2 text-sm text-gray-600">
              or drag and drop files here
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Maximum size: {(maxSize / 1048576).toFixed(1)}MB
              {accept && ` • Accepted: ${accept}`}
            </p>
          </div>
        </div>
      </div>

      {selectedFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          {selectedFiles.map((file, index) => {
            const progress = uploadProgress[file.originalFilename] || 100;
            return (
              <div
                key={file.id || index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {file.type === 'image' && file.thumbnailUrl ? (
                    <img
                      src={file.thumbnailUrl}
                      alt={file.originalFilename}
                      className="w-10 h-10 object-cover rounded flex-shrink-0"
                    />
                  ) : (
                    <svg
                      className="w-5 h-5 text-gray-400 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-700 truncate block">{file.originalFilename}</span>
                    <span className="text-xs text-gray-500">
                      ({(file.size / 1024).toFixed(1)} KB)
                    </span>
                    {uploading && progress < 100 && (
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(index)}
                  className="text-red-600 hover:text-red-800 ml-2"
                  aria-label={`Remove ${file.originalFilename}`}
                  disabled={uploading}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default FileUpload;

